import { startOfDay } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useLineTotals } from '~/hooks/useLineTotals';
import { DocumentBuilderForm, RelatedDocumentType } from '~/types/document-builder';
import { StampType, TipoCassa } from '~/types/invoicing';
import {
  calculateCompetencies,
  calculateVatItemsTotals,
  filterContributionBaseItems,
  isManagementDataStamp,
} from '~/utils/invoicing/items';
import {
  useRelatedDocumentsFieldArray,
  useWithholdingsFieldArray,
} from '~/views/invoices/builder/hooks/fieldArrayContext';
import { useAddWithholding } from '~/views/invoices/builder/hooks/withholding';

export const useMotivationState = () => {
  const { setValue } = useFormContext<DocumentBuilderForm>();
  const motivation = useWatch<DocumentBuilderForm, 'motivation'>({ name: 'motivation' });
  const motivationVisible = motivation !== null;
  const showMotivation = useCallback(() => setValue('motivation', ''), [setValue]);
  return useMemo(() => [motivationVisible, showMotivation] as const, [showMotivation, motivationVisible]);
};

export const useNotesState = () => {
  const { setValue, watch } = useFormContext<DocumentBuilderForm>();
  const hasNotes = watch('notes') !== null;
  const showNotes = useCallback(() => setValue('notes', ''), [setValue]);
  return useMemo(() => [hasNotes, showNotes] as const, [hasNotes, showNotes]);
};

export const useRiferimentoAmministrazioneState = () => {
  const { setValue } = useFormContext<DocumentBuilderForm>();
  const value = useWatch<DocumentBuilderForm, 'riferimentoAmministrazione'>({ name: 'riferimentoAmministrazione' });
  const isVisible = value !== null;
  const show = useCallback(() => setValue('riferimentoAmministrazione', ''), [setValue]);
  return { isVisible, show };
};

export const useSocialSecurityState = () => {
  const { getValues, setValue } = useFormContext<DocumentBuilderForm>();
  const socialSecurity = useWatch<DocumentBuilderForm, 'socialSecurity'>({ name: 'socialSecurity' });
  const lineTotals = useLineTotals();
  const socialSecurityVisible = socialSecurity !== null;

  const addSocialSecurity = useCallback(() => {
    const values = getValues();
    const taxableItems = filterContributionBaseItems(values.items, values.fiscalRegime);
    const linesTotals = taxableItems.map(lineTotals);
    const vatItemsTotals = calculateVatItemsTotals(values.items, values.currency, values.useGrossPrices);
    const netTotalAmount = calculateCompetencies(linesTotals, values.useGrossPrices, vatItemsTotals);

    setValue(
      'socialSecurity',
      {
        amountPercentage: '100',
        taxableAmount: netTotalAmount.toFixed(),
        type: TipoCassa.AvvocatiEProcuratori,
        percentage: '20',
      },
      { shouldValidate: true },
    );
  }, [getValues, lineTotals, setValue]);

  return useMemo(() => [socialSecurityVisible, addSocialSecurity] as const, [addSocialSecurity, socialSecurityVisible]);
};

export const useStampState = () => {
  const { setValue } = useFormContext<DocumentBuilderForm>();
  const stamp = useWatch<DocumentBuilderForm, 'stamp'>({ name: 'stamp' });
  const items = useWatch<DocumentBuilderForm, 'items'>({ name: 'items' });
  const stampVisible = stamp !== null || items.some((i) => i.managementData.some(isManagementDataStamp));

  const addStamp = useCallback(() => {
    setValue('stamp', { type: StampType.Issuer, value: '2.00' }, { shouldValidate: true });
  }, [setValue]);

  return useMemo(() => [stampVisible, addStamp] as const, [addStamp, stampVisible]);
};

export const useVatCollectionState = () => {
  const vatCollection = useWatch<DocumentBuilderForm, 'vatCollection'>({ name: 'vatCollection' });
  const [vatCollectionVisible, setVatCollectionVisible] = useState<boolean>(vatCollection !== null);
  useEffect(() => {
    if (vatCollection !== null && !vatCollectionVisible) {
      setVatCollectionVisible(true);
    }
  }, [vatCollection, vatCollectionVisible]);
  const showVatCollection = useCallback(() => setVatCollectionVisible(true), []);
  return useMemo(() => [vatCollectionVisible, showVatCollection] as const, [showVatCollection, vatCollectionVisible]);
};

export const useWithholdingState = () => {
  const withholdingsFieldArray = useWithholdingsFieldArray();
  const withholdingVisible = withholdingsFieldArray.fields.length > 0;
  const addWithholding = useAddWithholding();

  return useMemo(() => [withholdingVisible, addWithholding] as const, [addWithholding, withholdingVisible]);
};

export const useRelatedDocumentsState = () => {
  const relatedDocumentsFieldArray = useRelatedDocumentsFieldArray();
  const relatedVisible = relatedDocumentsFieldArray.fields.length > 0;

  const addRelated = useCallback(() => {
    const today = startOfDay(new Date());
    relatedDocumentsFieldArray.append({
      dbDocumentId: null,
      type: RelatedDocumentType.FatturaCollegata,
      documentNumber: '',
      date: today,
      CIGCode: '',
      CUPCode: '',
      orderCode: '',
    });
  }, [relatedDocumentsFieldArray]);

  return useMemo(() => [relatedVisible, addRelated] as const, [addRelated, relatedVisible]);
};
